/**
 * Foundation Layer 4 — Telemetry (web).
 *
 * Typed event bus + Sentry wiring. Every feature publishes *typed*
 * domain events here instead of each reaching for its own
 * `console.log`, `Sentry.captureException`, or `fetch('/analytics')`.
 *
 * Drift baseline at the moment this layer landed: 8,220+ untyped
 * `console.log` / `console.error` sites across web + iOS + Android
 * (per the handoff audit). Those retire into typed events —
 * `Event.entrySaved`, `Event.affiliateFunnelFired`, etc. — routed
 * through this single bus.
 *
 * Why a bus, not direct `Sentry.captureException` calls:
 *   - Features don't need to know that Sentry is the sink. Tomorrow
 *     it might be Datadog or GCP Logging; only this file changes.
 *   - Structured events carry tags that Sentry / analytics can index.
 *     Raw `console.log` throws away everything an on-call needs.
 *   - Subscribers can filter. A ConsoleSubscriber writes in dev; a
 *     SentrySubscriber fires only errors in prod; a MetricsSubscriber
 *     counts `entrySaved` per user.
 *   - Test seam: bus injected into features, subscriber replaced with
 *     an in-memory recorder. No Sentry in the test graph.
 *
 * Integrates with the onEvent hooks already exposed by Networking +
 * Auth — `wireGatewaysToBus(bus)` bridges them so network retries
 * and auth transitions become first-class telemetry events without
 * every caller having to subscribe manually.
 *
 * See `docs/LINKBOX_ARCHITECTURE.md` §2.
 */

// ─── Event shape ─────────────────────────────────────────────────────

/**
 * A closed union of every domain event the suite emits. Add new
 * events here (with their payload shape) rather than stringly-typing
 * them at the call site. The closed union is what makes this layer
 * actually *typed* — a subscriber gets an exhaustive switch over the
 * set and the compiler catches missing arms.
 */
export type TelemetryEvent =
    // ── Feature events ──────────────────────────────────────────
    | { kind: 'entry.saved'; entryId: string; userId: string; source: string }
    | { kind: 'entry.save_failed'; source: string; error: string }
    | { kind: 'entry.enrichment_fired'; entryId: string; userId: string }
    | { kind: 'affiliate.funnel_ran'; entryId: string; recommendationCount: number }
    | { kind: 'receipt.persisted'; receiptId: string; status: string }
    // ── Foundation-layer events (piped from Networking / Auth) ──
    | { kind: 'http.request'; source?: string; url: string; method: string }
    | { kind: 'http.response'; source?: string; url: string; status: number; latencyMs: number }
    | { kind: 'http.retry'; source?: string; url: string; attempt: number; reason: string }
    | { kind: 'http.circuit_open'; source?: string; host: string }
    | { kind: 'http.circuit_closed'; source?: string; host: string }
    | { kind: 'http.circuit_half_open'; source?: string; host: string }
    | { kind: 'http.error'; source?: string; url: string; error: unknown }
    | { kind: 'auth.signed_in'; userId: string }
    | { kind: 'auth.signed_out' }
    | { kind: 'auth.token_refreshed' }
    // ── Edge-function events (bridged from SupabaseGateway.invokeFunction) ──
    | { kind: 'edge.invoked'; name: string; source?: string }
    | { kind: 'edge.succeeded'; name: string; source?: string; latencyMs: number }
    | { kind: 'edge.failed'; name: string; source?: string; error: unknown }
    // ── Generic fallback for ad-hoc events during migration ─────
    | { kind: 'legacy'; message: string; attributes?: Record<string, unknown> }

export type EventSeverity = 'debug' | 'info' | 'warn' | 'error'

export interface SubscriberContext {
    severity: EventSeverity
}

export type Subscriber = (event: TelemetryEvent, context: SubscriberContext) => void

// ─── Event bus ───────────────────────────────────────────────────────

/**
 * In-process typed event bus. Stateless aside from its subscriber list.
 * Safe to share across the app; every feature depends on the same
 * singleton via `getEventBus()`.
 */
export class EventBus {
    private subscribers = new Set<Subscriber>()

    subscribe(subscriber: Subscriber): () => void {
        this.subscribers.add(subscriber)
        return () => {
            this.subscribers.delete(subscriber)
        }
    }

    publish(event: TelemetryEvent, severity: EventSeverity = inferSeverity(event)): void {
        const ctx: SubscriberContext = { severity }
        for (const subscriber of this.subscribers) {
            try {
                subscriber(event, ctx)
            } catch {
                // A misbehaving subscriber must never cascade into others.
            }
        }
    }

    clear(): void {
        this.subscribers.clear()
    }
}

function inferSeverity(event: TelemetryEvent): EventSeverity {
    switch (event.kind) {
        case 'entry.save_failed':
        case 'http.error':
        case 'http.circuit_open':
        case 'edge.failed':
            return 'error'
        case 'http.retry':
            return 'warn'
        case 'http.request':
        case 'http.response':
        case 'edge.invoked':
        case 'edge.succeeded':
            return 'debug'
        default:
            return 'info'
    }
}

// ─── Stock subscribers ──────────────────────────────────────────────

/**
 * Development-grade subscriber — pretty-prints events to the console.
 * Not used in production; Sentry + metrics subscribers replace it.
 */
export function consoleSubscriber(
    options: { minSeverity?: EventSeverity } = {},
): Subscriber {
    const min = options.minSeverity ?? 'info'
    const rank: Record<EventSeverity, number> = { debug: 0, info: 1, warn: 2, error: 3 }
    return (event, ctx) => {
        if (rank[ctx.severity] < rank[min]) return
        const fn =
            ctx.severity === 'error'
                ? console.error
                : ctx.severity === 'warn'
                    ? console.warn
                    : ctx.severity === 'debug'
                        ? console.debug
                        : console.log
        fn(`[telemetry:${ctx.severity}] ${event.kind}`, event)
    }
}

/**
 * Sentry bridge. Accepts any object that matches the sentry-style
 * capture surface — the real Sentry SDK, a wrapper, or a test spy.
 * Never imports `@sentry/nextjs` directly so tests can avoid pulling
 * the SDK into the dependency graph.
 */
export interface SentryLike {
    captureMessage: (message: string, level: 'debug' | 'info' | 'warning' | 'error' | 'fatal') => void
    captureException: (err: unknown, context?: { extra?: Record<string, unknown> }) => void
    addBreadcrumb: (bc: { category: string; message?: string; level?: string; data?: Record<string, unknown> }) => void
}

export function sentrySubscriber(sentry: SentryLike): Subscriber {
    return (event, ctx) => {
        // Errors → capture; everything else → breadcrumb trail.
        if (ctx.severity === 'error') {
            const maybeError = (event as any).error
            if (maybeError instanceof Error) {
                sentry.captureException(maybeError, { extra: { event } })
            } else {
                sentry.captureMessage(event.kind, 'error')
            }
            return
        }
        sentry.addBreadcrumb({
            category: categoryOf(event),
            message: event.kind,
            level: sentryLevel(ctx.severity),
            data: event as unknown as Record<string, unknown>,
        })
    }
}

function categoryOf(event: TelemetryEvent): string {
    const dot = event.kind.indexOf('.')
    return dot > 0 ? event.kind.slice(0, dot) : event.kind
}

function sentryLevel(severity: EventSeverity): string {
    if (severity === 'warn') return 'warning'
    return severity
}

// ─── Recorder for tests ─────────────────────────────────────────────

export class RecordingSubscriber {
    readonly events: Array<{ event: TelemetryEvent; severity: EventSeverity }> = []

    subscribe(bus: EventBus): () => void {
        return bus.subscribe((event, ctx) => this.events.push({ event, severity: ctx.severity }))
    }

    ofKind<K extends TelemetryEvent['kind']>(kind: K): Array<Extract<TelemetryEvent, { kind: K }>> {
        return this.events.filter(e => e.event.kind === kind).map(e => e.event) as Array<
            Extract<TelemetryEvent, { kind: K }>
        >
    }
}

// ─── Gateway wiring ─────────────────────────────────────────────────

/**
 * Bridges the Networking + Auth foundation layers to this bus so
 * network retries, circuit transitions, auth state changes etc. flow
 * through telemetry without every caller wiring a subscriber.
 *
 * Call once at app bootstrap:
 *   const bus = getEventBus()
 *   wireGatewaysToBus({ bus, http: httpClient, auth: getAuthGateway() })
 */
export interface WireTargets {
    bus: EventBus
    http?: {
        // Loose-typed — accepts any client with an onEvent-style setter.
        setEventListener?: (listener: (e: unknown) => void) => void
    }
    auth?: {
        subscribe: (listener: (e: unknown) => void) => () => void
    }
}

export function wireGatewaysToBus(targets: WireTargets): () => void {
    const { bus, http, auth } = targets
    const disposers: Array<() => void> = []

    if (http?.setEventListener) {
        http.setEventListener((e: any) => {
            switch (e?.kind) {
                case 'request':
                    bus.publish({ kind: 'http.request', source: e.source, url: e.url, method: e.method })
                    break
                case 'response':
                    bus.publish({
                        kind: 'http.response',
                        source: e.source,
                        url: e.url,
                        status: e.status,
                        latencyMs: e.latencyMs,
                    })
                    break
                case 'retry':
                    bus.publish({
                        kind: 'http.retry',
                        source: e.source,
                        url: e.url,
                        attempt: e.attempt,
                        reason: e.reason,
                    })
                    break
                case 'circuit_open':
                    bus.publish({ kind: 'http.circuit_open', source: e.source, host: e.host })
                    break
                case 'error':
                    bus.publish({ kind: 'http.error', source: e.source, url: e.url, error: e.error })
                    break
            }
        })
    }

    if (auth) {
        const off = auth.subscribe((e: any) => {
            switch (e?.kind) {
                case 'signed_in':
                    bus.publish({ kind: 'auth.signed_in', userId: e.user?.id ?? 'unknown' })
                    break
                case 'signed_out':
                    bus.publish({ kind: 'auth.signed_out' })
                    break
                case 'token_refreshed':
                    bus.publish({ kind: 'auth.token_refreshed' })
                    break
            }
        })
        disposers.push(off)
    }

    return () => disposers.forEach(fn => fn())
}

// ─── Singleton + test seam ──────────────────────────────────────────

let sharedBus: EventBus | null = null

export function getEventBus(): EventBus {
    if (!sharedBus) sharedBus = new EventBus()
    return sharedBus
}

export function setEventBusForTesting(bus: EventBus): void {
    sharedBus = bus
}

export function resetEventBusForTesting(): void {
    sharedBus = null
}
